import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import i18next from "i18next";
import { Controller } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { FIELD_REQUIRED, LOADING } from "../../i18n/keysTranslations";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import { getReactHookFormNestedError } from "../../utils/errors";
import { INPUT_COLOR_BORDER, PRIMARY_COLOR_TEXT } from "../../utils/colors";
import { startGetSubscriptionsStripePaginated } from "../../actions/owner";

const LOADING_MORE_OPTION_ID = "__loading_more_option__";
const LOAD_MORE_TRIGGER_PX = 48;
const WHITE_COLOR = "#fff";
const EMPTY_VALUE = "-";

const buildLoadingOption = () => ({
  subscriptionID: LOADING_MORE_OPTION_ID,
  customerName: "",
  customerEmail: "",
  productName: "",
  disabled: true,
  isLoadingOption: true,
});

const getDisplayName = (option = {}) =>
  option.customerName?.trim() || option.customerEmail || EMPTY_VALUE;

const isLoadMoreTriggered = (listboxNode) => {
  const scrollBottom =
    listboxNode.scrollHeight - listboxNode.scrollTop - listboxNode.clientHeight;
  return scrollBottom < LOAD_MORE_TRIGGER_PX;
};

const SelectSubscriptionStripe = ({
  control,
  errors = {},
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled = false,
  placeholder,
  variant = "filled",
  fullWidth,
  helperText,
  sxTextField = {},
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastKey, setLastKey] = useState(null);
  const hasLoadedFirstPageRef = useRef(false);
  const isFetchingFirstPageRef = useRef(false);
  const isFetchingMoreRef = useRef(false);
  const listboxRef = useRef(null);
  const savedScrollTopRef = useRef(0);
  const shouldRestoreScrollRef = useRef(false);

  const error = getReactHookFormNestedError(errors, name);
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const optionsBySubscriptionID = useMemo(
    () =>
      new Map(
        options
          .map((item) => [item.subscriptionID, item])
          .filter(([id]) => !!id)
      ),
    [options]
  );

  const mergeUniqueSubscriptions = useCallback((currentOptions, incoming) => {
    const optionsByID = new Map(
      currentOptions.map((item) => [item.subscriptionID, item])
    );

    incoming.forEach((item) => {
      if (!item?.subscriptionID) return;
      optionsByID.set(item.subscriptionID, item);
    });

    return Array.from(optionsByID.values());
  }, []);

  const fetchFirstSubscriptionsPage = useCallback(async () => {
    if (
      hasLoadedFirstPageRef.current ||
      isFetchingFirstPageRef.current ||
      loading
    ) {
      return;
    }

    isFetchingFirstPageRef.current = true;
    setLoading(true);

    try {
      const response = await dispatch(
        startGetSubscriptionsStripePaginated({
          lastKey: null,
        })
      );

      if (response?.ok) {
        const payload = response?.data || {};
        const items = Array.isArray(payload?.items) ? payload.items : [];
        const nextLastKey = payload?.lastKey || null;
        const nextHasMore = Boolean(payload?.hasMore);

        setLastKey(nextLastKey);
        setHasMore(nextHasMore);
        setOptions(items);
      } else {
        setOptions([]);
        setLastKey(null);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
      hasLoadedFirstPageRef.current = true;
      isFetchingFirstPageRef.current = false;
    }
  }, [dispatch, loading]);

  const fetchMoreSubscriptions = useCallback(async () => {
    if (
      !hasMore ||
      !lastKey ||
      loading ||
      loadingMore ||
      isFetchingMoreRef.current
    ) {
      return;
    }

    isFetchingMoreRef.current = true;
    shouldRestoreScrollRef.current = true;
    setLoadingMore(true);

    try {
      const response = await dispatch(
        startGetSubscriptionsStripePaginated({
          lastKey,
        })
      );

      if (response?.ok) {
        const payload = response?.data || {};
        const items = Array.isArray(payload?.items) ? payload.items : [];
        const nextLastKey = payload?.lastKey || null;
        const nextHasMore = Boolean(payload?.hasMore);

        setLastKey(nextLastKey);
        setHasMore(nextHasMore);
        setOptions((previous) => mergeUniqueSubscriptions(previous, items));
      }
    } finally {
      setLoadingMore(false);
      isFetchingMoreRef.current = false;
    }
  }, [
    dispatch,
    hasMore,
    lastKey,
    loading,
    loadingMore,
    mergeUniqueSubscriptions,
  ]);

  useEffect(() => {
    if (open && !hasLoadedFirstPageRef.current) {
      fetchFirstSubscriptionsPage();
    }
  }, [open, fetchFirstSubscriptionsPage]);

  useLayoutEffect(() => {
    if (!shouldRestoreScrollRef.current) return;
    if (loadingMore) return;
    if (!listboxRef.current) return;

    const targetScrollTop = savedScrollTopRef.current;
    requestAnimationFrame(() => {
      if (listboxRef.current) {
        listboxRef.current.scrollTop = targetScrollTop;
      }
      requestAnimationFrame(() => {
        if (listboxRef.current) {
          listboxRef.current.scrollTop = targetScrollTop;
        }
        shouldRestoreScrollRef.current = false;
      });
    });
  }, [options, loadingMore]);

  const handleScrollListbox = useCallback(
    (event) => {
      if (!hasMore || loading || loadingMore) return;

      const listboxNode = event.currentTarget;
      if (isLoadMoreTriggered(listboxNode)) {
        listboxRef.current = listboxNode;
        savedScrollTopRef.current = listboxNode.scrollTop;
        fetchMoreSubscriptions();
      }
    },
    [hasMore, loading, loadingMore, fetchMoreSubscriptions]
  );

  const optionsWithLoading = loadingMore
    ? [...options, buildLoadingOption()]
    : options;

  return (
    <>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field }) => {
          const selectedOption =
            (field?.value && optionsBySubscriptionID.get(field.value)) || null;

          return (
            <Autocomplete
              {...field}
              value={selectedOption}
              id={name}
              fullWidth={fullWidth}
              disabled={disabled}
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              options={optionsWithLoading}
              loading={loading}
              ListboxProps={{
                onScroll: handleScrollListbox,
                ref: listboxRef,
              }}
              loadingText={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress color="inherit" size={20} />
                  {t(LOADING)}
                </Box>
              }
              noOptionsText={loading ? t(LOADING) : ""}
              getOptionDisabled={(option) =>
                Boolean(option?.disabled || option?.isLoadingOption)
              }
              isOptionEqualToValue={(option, value) =>
                option?.subscriptionID === value?.subscriptionID
              }
              getOptionLabel={(option) => {
                if (!option) return "";
                if (option?.isLoadingOption) {
                  return t(LOADING);
                }
                if (typeof option === "string") {
                  const matched = optionsBySubscriptionID.get(option);
                  if (!matched) return "";
                  return `${getDisplayName(matched)} - ${matched.subscriptionID}`;
                }
                if (!option?.subscriptionID) return "";
                return `${getDisplayName(option)} - ${option.subscriptionID}`;
              }}
              onChange={(_, value) => {
                if (value?.subscriptionID && !value?.isLoadingOption) {
                  field.onChange(value.subscriptionID);
                  return;
                }
                field.onChange(null);
              }}
              renderOption={(props, option) => {
                if (option?.isLoadingOption) {
                  return (
                    <Box
                      {...props}
                      component="li"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        backgroundColor: WHITE_COLOR,
                        opacity: "1 !important",
                        cursor: "default !important",
                        "&.Mui-disabled": {
                          opacity: "1 !important",
                          color: PRIMARY_COLOR_TEXT,
                          backgroundColor: WHITE_COLOR,
                          WebkitTextFillColor: PRIMARY_COLOR_TEXT,
                        },
                        "&:hover": {
                          backgroundColor: WHITE_COLOR,
                        },
                      }}
                    >
                      <CircularProgress
                        size={16}
                        sx={{ color: PRIMARY_COLOR_TEXT }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: PRIMARY_COLOR_TEXT }}
                      >
                        {t(LOADING)}
                      </Typography>
                    </Box>
                  );
                }
                return (
                  <Box
                    {...props}
                    component="li"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start !important",
                      opacity: option?.disabled ? 0.55 : 1,
                    }}
                  >
                    <Typography variant="body1">
                      {option?.customerName || EMPTY_VALUE}
                    </Typography>
                    <Typography variant="body2">
                      {option?.customerEmail || EMPTY_VALUE}
                    </Typography>
                    <Typography variant="caption">
                      {option?.productName || EMPTY_VALUE}
                    </Typography>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant={variant}
                  placeholder={field?.value ? "" : placeholder}
                  error={!!error}
                  label={label}
                  hiddenLabel={!label}
                  sx={{
                    backgroundColor: "fields.background",
                    border: `1px solid ${INPUT_COLOR_BORDER}`,
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                    ...sxTextField,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          );
        }}
      />
      {(error?.message || helperText) && (
        <Typography
          variant="caption"
          color={error?.message ? "error" : "fields.placeholder"}
        >
          {error?.message || helperText}
        </Typography>
      )}
    </>
  );
};

export default SelectSubscriptionStripe;
